"use client";

import { useGame } from "@/app/providers/GameContextProvider";
import React, { useEffect, useState, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import GameModesSoniop from "./components/GameModes/GameModesSoniop";
import Lobby from "./components/Lobby/Lobby";
import { createGameRoom, joinGameRoom } from "@/lib/utils/roomUtils";
import Loading from "./components/Lobby/Loading";
import GamePlay from "./components/GamePlay/GamePlay";
import GameResult from "./components/GameResult/GameResult";
import { GameRecap } from "./components/GameRecap";
import { WinnerScreen } from "./components/WinnerScreen";
import ErrorBoundary from "./components/GameResult/ErrorBoundary";
import { GameStep } from "@/lib/types/common/GameStep";
import BackgroundEther from "./components/BackgroundEther";
import { getBackendUrl } from "@/lib/utils/imageUrls";
import ServerInfoBar from "./components/GamePlay/ServerInfoBar/ServerInfoBar";
import DevPanel from "./components/DevTools/DevPanel";
import DevModeIndicator from "./components/DevTools/DevModeIndicator";
import { usePhotosphereQuality } from "@/app/providers/PhotosphereQualityProvider";
import Compass from "@/app/components/Compass/Compass";
import { GameHazard, GameMode } from "@/lib/types/common/GameMode";
import { CoopRole } from "@/lib/types/common/Player";
import { yawStore } from "@/app/hooks/useYawStore";
import FlashEmptyPhotosphere from "./components/GamePlay/FlashEmptyPhotosphere";
import RoundCountdown from "./components/GamePlay/RoundCountdown";
import { PhotoSphereViewer, PhotoSphereViewerAPI } from "@/app/components/PhotoSphereViewer";
import Navbar from "./components/Navbar";
import { getRandomWallpaper } from "@/lib/utils/wallpaperUtils";
import Image from "next/image";

const Page = () => {
    const { client, room, setRoom, gameState, showWinnerScreen, setShowWinnerScreen, setPhotosphereLoading } = useGame();
    const { getPhotosphereUrl, quality } = usePhotosphereQuality();
    const [isFlashVisible, setIsFlashVisible] = useState(true);
    const [showCountdown, setShowCountdown] = useState(false);
    const [countdownComplete, setCountdownComplete] = useState(false);
    const [hideHazardsInResult, setHideHazardsInResult] = useState(false);
    const [showResultOverlay, setShowResultOverlay] = useState(true);
    const viewerRef = useRef<PhotoSphereViewerAPI>(null);
    const flashTimerRef = useRef<NodeJS.Timeout | null>(null);
    const photosphereLoadedSentRef = useRef<string | null>(null); // Track which photosphere we've sent loaded for
    const [randomizedYaw, setRandomizedYaw] = useState<number>(-Math.PI / 2);
    const seedFromUrlRef = useRef<string | null>(null); // Store seed parameter from initial URL


    // Reset view state when photosphere changes
    useEffect(() => {
        if (gameState?.currentPhotosphere?.id) {
            yawStore.reset();
            setHideHazardsInResult(false);
            setIsFlashVisible(true); // Reset flash visibility
            setCountdownComplete(false); // Reset countdown state
            photosphereLoadedSentRef.current = null; // Reset the loaded sent tracker
            // Reset viewer position
            viewerRef.current?.reset();
            
            // Randomize yaw if compass is disabled in custom mode
            const isCompassDisabled = gameState?.gameMode === GameMode.CUSTOM && gameState?.customModeSettings?.compassDisabled;
            if (isCompassDisabled) {
                // Generate deterministic random yaw based on game seed and round
                // This ensures all players see the same starting orientation
                const seed = gameState.gameSettings?.gameSeed || '';
                const round = gameState.currentRound || 0;
                const seedString = `${seed}-${round}-yaw`;
                
                // Simple hash function to generate deterministic random number
                let hash = 0;
                for (let i = 0; i < seedString.length; i++) {
                    const char = seedString.charCodeAt(i);
                    hash = ((hash << 5) - hash) + char;
                    hash = hash & hash;
                }
                
                // Convert hash to a value between 0 and 2π
                const normalizedHash = Math.abs(hash) / 2147483647; // Normalize to 0-1
                const randomYaw = normalizedHash * Math.PI * 2;
                setRandomizedYaw(randomYaw);
            } else {
                // Default yaw (north)
                setRandomizedYaw(-Math.PI / 2);
            }
        }
    }, [gameState?.currentPhotosphere?.id, gameState?.gameMode, gameState?.customModeSettings?.compassDisabled, gameState?.currentRound, gameState?.gameSettings?.gameSeed]);

    // Show countdown for Timed, Flash, HAZARD, and Daily modes when entering GUESSING step
    // Also show for Custom mode
    // The countdown component now handles waiting for players internally
    useEffect(() => {
        const shouldShowCountdown = gameState?.gameMode === GameMode.TIMED ||
            gameState?.gameMode === GameMode.FLASH ||
            gameState?.gameMode === GameMode.HAZARD ||
            gameState?.gameMode === GameMode.DAILY ||
            gameState?.gameMode === GameMode.FIGHT ||
            gameState?.gameMode === GameMode.COOP ||
            (gameState?.gameMode === GameMode.CUSTOM && (gameState.gameSettings?.timerSeconds ?? 0) > 0);
        // Show countdown immediately when entering GUESSING step
        // The countdown component will show "waiting for players" until all are ready
        if (gameState?.gameStep === GameStep.GUESSING && shouldShowCountdown) {
            setShowCountdown(true);
            setCountdownComplete(false);
        } else if (gameState?.gameStep !== GameStep.GUESSING) {
            setShowCountdown(false);
        }
    }, [gameState?.gameStep, gameState?.gameMode, gameState?.currentPhotosphere?.id, gameState?.gameSettings?.timerSeconds]);

    // Helper to check if flash mode is active (Flash mode or Custom with hasFlashMode)
    const isFlashModeActive = gameState?.gameMode === GameMode.FLASH ||
        (gameState?.gameMode === GameMode.CUSTOM && gameState?.gameSettings?.hasFlashMode);

    // Helper to check if hazard mode is active (Hazard mode or Custom with hasHazards)
    const isHazardModeActive = gameState?.gameMode === GameMode.HAZARD ||
        (gameState?.gameMode === GameMode.CUSTOM && gameState?.gameSettings?.hasHazards);

    // Flash mode: Start timer when countdown completes
    useEffect(() => {
        if (countdownComplete && gameState?.gameStep === GameStep.GUESSING && isFlashModeActive && !flashTimerRef.current) {
            console.log("⚡ Flash mode: Countdown complete, starting flash timer");
            // Get flash duration from either Flash mode settings or Custom mode settings
            const flashDuration = gameState?.gameMode === GameMode.FLASH
                ? gameState?.flashModeSettings?.flashDuration
                : gameState?.gameSettings?.flashDuration;
            if (flashDuration) {
                setIsFlashVisible(true);
                flashTimerRef.current = setTimeout(() => {
                    setIsFlashVisible(false);
                    flashTimerRef.current = null;
                }, flashDuration + 100);
            }
        }
    }, [countdownComplete, gameState?.gameStep, gameState?.gameMode, isFlashModeActive]);

    const handleStart = async () => {
        if (client && !room) {
            try {
                const newRoom = await createGameRoom(client);
                console.log("🎮 Created room Room ID:", newRoom.roomId);
                setRoom(newRoom);
            } catch (err) {
                console.error("❌ Failed to create room:", err);
            }
        }
    };

    // Join room by id or create new one
    useEffect(() => {
        if (client && !room && typeof window !== 'undefined') {
            const params = new URLSearchParams(window.location.search);
            const roomId = params.get("id");
            const seed = params.get("seed");
            
            // Store seed parameter if present
            if (seed && !seedFromUrlRef.current) {
                seedFromUrlRef.current = seed;
                console.log("📦 Stored seed parameter from URL:", seed);
            }

            if (roomId) {
                console.log("🔗 Attempting to join room:", roomId);
                joinGameRoom(client, roomId)
                    .then((joinedRoom) => {
                        console.log("✅ Successfully joined room:", roomId);
                        setRoom(joinedRoom);
                    })
                    .catch((err) => {
                        console.log("❌ Failed to join room:", roomId, "Creating new room instead", err);
                        handleStart();
                    });
            } else {
                console.log("🆕 No room ID provided, creating new room");
                handleStart();
            }
        }
    }, [client, room]);

    // Update URL when room changes (preserve seed parameter if present)
    useEffect(() => {
        if (room && typeof window !== 'undefined') {
            const url = new URL(window.location.href);
            url.searchParams.set('id', room.roomId);
            // Re-add seed parameter if it was stored
            if (seedFromUrlRef.current) {
                url.searchParams.set('seed', seedFromUrlRef.current);
            }
            window.history.replaceState({}, '', url.toString());
        }
    }, [room]);

    // Handle seed URL parameter
    useEffect(() => {
        if (room && gameState && typeof window !== 'undefined') {
            const seed = seedFromUrlRef.current; // Use stored seed
            const isGameMaster = room.sessionId === gameState.gameMasterId;
            
            // Only process seed parameter for game master and if we're still in lobby
            if (seed && isGameMaster && gameState.gameStep === GameStep.LOBBY && !gameState.showSeedPanel) {
                console.log("🎲 Detected seed parameter:", seed);
                // Open seed panel
                room.send("toggleSeedPanel", { show: true });
                // Set seed input
                setTimeout(() => {
                    room.send("updateSeedInput", { seed: seed });
                }, 100);
                // Clear the stored seed and remove from URL to avoid reopening
                seedFromUrlRef.current = null;
                const url = new URL(window.location.href);
                url.searchParams.delete('seed');
                window.history.replaceState({}, '', url.toString());
            }
        }
    }, [room, gameState?.gameStep, gameState?.gameMasterId, gameState?.showSeedPanel]);

    // Reset current yaw when a new round starts
    useEffect(() => {
        yawStore.reset();
        viewerRef.current?.reset();
    }, [gameState?.currentRound]);

    return (
        <div className="relative font-myriad h-full w-full overflow-hidden flex flex-col anti-flashbang">
            {/* Development Tools */}
            <DevPanel />
            <DevModeIndicator />

            <AnimatePresence mode="wait">
                {/* Background Layer - Ether */}
                {(() => {
                    // Check if current player is in individual lobby mode
                    const currentPlayer = gameState?.players?.get(room?.sessionId || "");
                    const isInIndividualLobby = currentPlayer?.inLobby && gameState?.gameStep === GameStep.RECAP;
                    // Show background ether during loading, lobby, or individual lobby
                    const showBackground = !room || gameState?.gameStep === undefined || gameState?.gameStep === GameStep.LOBBY || isInIndividualLobby;

                    return showBackground && <BackgroundEther />;
                })()}
            </AnimatePresence>

            <AnimatePresence mode="wait">
                {!room && (
                    <Loading key="loading" />
                )}
                {room && (() => {
                    // Check if current player is in individual lobby mode
                    const currentPlayer = gameState?.players?.get(room.sessionId || "");
                    const isInIndividualLobby = currentPlayer?.inLobby && gameState?.gameStep === GameStep.RECAP;

                    // Determine effective game step for UI rendering
                    const effectiveGameStep = isInIndividualLobby ? GameStep.LOBBY : gameState?.gameStep;

                    // Check for Coop mode (including Custom with Coop baseMode)
                    const isCoopModeActive = gameState?.gameMode === GameMode.COOP ||
                        (gameState?.gameMode === GameMode.CUSTOM && gameState?.gameSettings?.baseMode === GameMode.COOP);
                    const isMapRole = isCoopModeActive && currentPlayer?.coopRole === CoopRole.MAP;

                    return (
                        <motion.div
                            key="main"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="relative w-full h-full origin-center"
                        >
                            <Navbar />

                            {/* ServerInfoBar - always visible */}
                            <ServerInfoBar />
                            {/* Persistent Photosphere - shown during GUESSING, RESULT, and RECAP */}
                            <AnimatePresence>
                                {(effectiveGameStep === GameStep.GUESSING || effectiveGameStep === GameStep.RESULT || (effectiveGameStep === GameStep.RECAP && showWinnerScreen)) && gameState?.currentPhotosphere && gameState.currentPhotosphere.id && (
                                    <>
                                        {/* Countdown overlay - includes waiting for players state internally */}
                                        <AnimatePresence>
                                            {showCountdown && !countdownComplete && effectiveGameStep === GameStep.GUESSING && (
                                                <RoundCountdown
                                                    onComplete={() => {
                                                        setCountdownComplete(true);
                                                        setShowCountdown(false);
                                                    }}
                                                />
                                            )}
                                        </AnimatePresence>

                                        {/* Background for MAP role players in Coop mode - random loading art (only during GUESSING) */}
                                        {isMapRole && effectiveGameStep === GameStep.GUESSING && (
                                            <motion.div
                                                key={`map-role-background-${gameState.currentPhotosphere.id}`}
                                                className="absolute inset-0 w-full h-full"
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                exit={{ opacity: 0 }}
                                            >
                                                <Image
                                                    src={getRandomWallpaper(
                                                        Array.from(gameState.selectedExpansions || []),
                                                        gameState.gameSettings?.gameSeed || "default",
                                                        gameState.currentRound || 1
                                                    )}
                                                    alt="Loading Background"
                                                    layout="fill"
                                                    objectFit="cover"
                                                    quality={100}
                                                    className="absolute inset-0 z-0 blur-sm"
                                                />
                                                <div className="absolute inset-0 z-10 bg-gradient-to-t from-[#030b18]/80 to-transparent"></div>
                                            </motion.div>
                                        )}

                                        {/* Photosphere viewer - shown for non-MAP role players always, and MAP role players during RESULT/RECAP */}
                                        {(!isMapRole || effectiveGameStep !== GameStep.GUESSING) && (
                                            <motion.div
                                                key={`photosphere-guessing-${gameState.currentPhotosphere.id}`}
                                                className="absolute inset-0 w-full h-full"
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: (!showCountdown && (isFlashVisible || gameState?.gameStep !== GameStep.GUESSING)) ? 1 : 0 }}
                                                exit={{ opacity: 0 }}
                                            >
                                                <PhotoSphereViewer
                                                    ref={viewerRef}
                                                    key={`viewer-guessing-${gameState.currentPhotosphere.id}${!isFlashModeActive ? `-${quality}` : ''}${isHazardModeActive ? `-${gameState.currentHazards.join('-')}` : ''}`}
                                                    src={isFlashModeActive
                                                        ? `${getBackendUrl()}/images/photospheres/${gameState.currentPhotosphere.id}/panorama_light.webp`
                                                        : getPhotosphereUrl(gameState.currentPhotosphere.id)}
                                                    height="100vh"
                                                    width="100%"
                                                    defaultYaw={randomizedYaw}
                                                    hazards={gameState.currentHazards as unknown as GameHazard[]}
                                                    showHazards={isHazardModeActive && !hideHazardsInResult}
                                                    // shader is now auto-selected in PhotoSphereViewer based on hazards
                                                    shaderSettings={{ pixelSize: 8 }}
                                                    onReady={() => {
                                                        setPhotosphereLoading(false);
                                                        // Notify server that this player has loaded the photosphere
                                                        // Only send once per photosphere to avoid duplicate messages
                                                        const currentPhotoId = gameState?.currentPhotosphere?.id;
                                                        if (room && currentPhotoId && photosphereLoadedSentRef.current !== currentPhotoId) {
                                                            photosphereLoadedSentRef.current = currentPhotoId;
                                                            room.send("photosphereLoaded", {});
                                                        }
                                                    }}
                                                />
                                            </motion.div>
                                        )}

                                        {/* Flash Mode: Show black screen when photosphere is hidden */}
                                        {isFlashModeActive && !isFlashVisible && effectiveGameStep === GameStep.GUESSING && (
                                            <FlashEmptyPhotosphere />
                                        )}

                                        {/* Compass - only visible during GUESSING if not disabled in custom mode */}
                                        <AnimatePresence>
                                            {(effectiveGameStep === GameStep.GUESSING || (effectiveGameStep === GameStep.RESULT && !showResultOverlay)) && 
                                             !(gameState?.gameMode === GameMode.CUSTOM && gameState?.customModeSettings?.compassDisabled) && (
                                                <Compass />
                                            )}
                                        </AnimatePresence>
                                    </>
                                )}
                            </AnimatePresence>

                            <Lobby />

                            <AnimatePresence mode="wait">
                                {effectiveGameStep === GameStep.LOBBY && (
                                    <>
                                        <GameModesSoniop key="game-modes" />
                                        <motion.div
                                            initial={{ y: "100%", opacity: 0.5 }}
                                            animate={{ y: "100%", opacity: 0.5, transition: { delay: 1 } }}
                                            whileHover={{ y: "20%", opacity: 1, transition: { delay: 0 } }}
                                            className="absolute bottom-[48px] right-4 hidden md:block w-[100px] h-[100px] cursor-pointer z-[100]"
                                        >
                                            <a href="/about" target="_blank" rel="noopener noreferrer">
                                                <Image
                                                    width={100}
                                                    height={100}
                                                    src="/about/moogle.png"
                                                    alt="Moogle to /About"
                                                />
                                            </a>
                                        </motion.div>
                                    </>
                                )}
                                {effectiveGameStep === GameStep.GUESSING && (
                                    <ErrorBoundary key="game-guessing">
                                        <GamePlay key="game-play" countdownComplete={countdownComplete} />
                                    </ErrorBoundary>
                                )}
                                {effectiveGameStep === GameStep.RESULT && (
                                    <GameResult
                                        key="game-result"
                                        showResultOverlay={showResultOverlay}
                                        setShowResultOverlay={setShowResultOverlay}
                                        onHideHazardsChange={setHideHazardsInResult}
                                    />
                                )}
                                {effectiveGameStep === GameStep.RECAP && (
                                    <AnimatePresence mode="wait">
                                        {showWinnerScreen ? (
                                            <WinnerScreen
                                                key="winner-screen"
                                                onViewGameRecap={() => setShowWinnerScreen(false)}
                                            />
                                        ) : (
                                            <ErrorBoundary key="game-recap">
                                                <GameRecap key="game-recap" />
                                            </ErrorBoundary>
                                        )}
                                    </AnimatePresence>
                                )}
                                {effectiveGameStep &&
                                    ![GameStep.LOBBY, GameStep.GUESSING, GameStep.RESULT, GameStep.RECAP].includes(effectiveGameStep) && (
                                        <div key="unknown-state" className="absolute inset-0 flex z-20 items-center justify-center text-white">
                                            <div>Game State: {effectiveGameStep}</div>
                                        </div>
                                    )}
                            </AnimatePresence>
                        </motion.div>
                    );
                })()}
            </AnimatePresence>
        </div>
    );
};

export default Page;
